import { useState, useMemo } from "react";
import { Copy, Check, Mail, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";

// Build the raw HTML for the email signature.
// Uses table layout + inline styles — the only reliable approach for Gmail/Outlook/Apple Mail.
function buildSignatureHTML({ name, title }) {
  const safeName = name || "Your Name";
  const safeTitle = title || "Sales Consultant";
  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;color:#111111;font-size:13px;line-height:1.5;">
  <tr>
    <td style="padding-right:18px;border-right:2px solid #D4AF37;vertical-align:top;">
      <table cellpadding="0" cellspacing="0" border="0">
        <tr>
          <td>
            <svg width="52" height="52" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
              <circle cx="13" cy="13" r="7.5" stroke="#D4AF37" stroke-width="2.2" fill="none"/>
              <circle cx="13" cy="13" r="2.3" fill="#D4AF37"/>
              <line x1="18.5" y1="18.5" x2="33" y2="33" stroke="#D4AF37" stroke-width="2.6" stroke-linecap="round"/>
              <line x1="26" y1="28" x2="30" y2="24" stroke="#D4AF37" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </td>
        </tr>
      </table>
    </td>
    <td style="padding-left:18px;vertical-align:top;">
      <div style="font-size:16px;font-weight:bold;color:#111111;margin-bottom:2px;">${safeName}</div>
      <div style="font-size:12px;color:#666666;margin-bottom:10px;">${safeTitle}</div>
      <div style="font-size:14px;font-weight:bold;letter-spacing:0.5px;color:#111111;">
        KEYS TO <span style="color:#D4AF37;font-style:italic;">OWN</span>
      </div>
      <div style="font-size:11px;color:#888888;font-style:italic;margin-top:1px;margin-bottom:10px;">Drive now. Own outright.</div>
      <table cellpadding="0" cellspacing="0" border="0" style="font-size:12px;color:#333333;">
        <tr>
          <td style="padding:1px 8px 1px 0;color:#D4AF37;font-weight:bold;">Phone</td>
          <td style="padding:1px 0;"><a href="tel:0422254888" style="color:#111111;text-decoration:none;">0422 254 888</a></td>
        </tr>
        <tr>
          <td style="padding:1px 8px 1px 0;color:#D4AF37;font-weight:bold;">Email</td>
          <td style="padding:1px 0;"><a href="mailto:admin@keystoown.au" style="color:#111111;text-decoration:none;">admin@keystoown.au</a></td>
        </tr>
        <tr>
          <td style="padding:1px 8px 1px 0;color:#D4AF37;font-weight:bold;">Web</td>
          <td style="padding:1px 0;"><a href="https://keystoown.au" style="color:#111111;text-decoration:none;">keystoown.au</a></td>
        </tr>
        <tr>
          <td style="padding:1px 8px 1px 0;color:#D4AF37;font-weight:bold;">ABN</td>
          <td style="padding:1px 0;color:#333333;">65 677 986 463</td>
        </tr>
      </table>
      <div style="font-size:10px;color:#999999;margin-top:12px;letter-spacing:0.5px;text-transform:uppercase;">
        For Australian ABN Holders &middot; No Credit Checks &middot; Brand-New Vehicles
      </div>
    </td>
  </tr>
</table>`;
}

export default function EmailSignature() {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("Sales Consultant");
  const [copied, setCopied] = useState(false);

  const html = useMemo(() => buildSignatureHTML({ name, title }), [name, title]);

  const copyHTML = async () => {
    try {
      // Copy as rich HTML so pasting into Gmail keeps formatting.
      if (window.ClipboardItem) {
        const blob = new Blob([html], { type: "text/html" });
        const textBlob = new Blob([html], { type: "text/plain" });
        await navigator.clipboard.write([
          new ClipboardItem({ "text/html": blob, "text/plain": textBlob }),
        ]);
      } else {
        await navigator.clipboard.writeText(html);
      }
      setCopied(true);
      toast.success("Signature HTML copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — select the HTML manually and copy");
    }
  };

  return (
    <div data-testid="email-signature-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <Link
            to="/admin"
            data-testid="signature-back-to-admin"
            className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-10">
          <div className="label-eyebrow mb-3">Utilities</div>
          <h1 className="font-display text-4xl md:text-5xl text-white" style={{ fontWeight: 300 }}>
            Email <span className="italic text-[#D4AF37]">Signature</span>
          </h1>
          <p className="text-white/60 mt-4 max-w-2xl">
            Customise your name and title below, then click <b>Copy HTML</b> and paste it into
            Gmail or Outlook. Works in every major email client.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Editor */}
          <aside className="lg:col-span-4 space-y-5">
            <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-5">
              <div className="label-eyebrow">Your Details</div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 block">
                  Full Name
                </span>
                <input
                  data-testid="sig-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Alex Reid"
                  className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-2 block">
                  Job Title
                </span>
                <input
                  data-testid="sig-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Sales Consultant"
                  className="w-full bg-[#050505] border border-white/10 text-white px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                />
              </label>
              <button
                onClick={copyHTML}
                data-testid="sig-copy"
                className={`w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                  copied
                    ? "bg-emerald-500 text-white"
                    : "bg-[#D4AF37] text-black hover:bg-[#F6ECCC]"
                }`}
              >
                {copied ? (<><Check size={16} /> Copied!</>) : (<><Copy size={16} /> Copy HTML</>)}
              </button>
            </div>

            {/* Install instructions */}
            <div className="bg-[#0a0a0a] border border-white/10 p-6 space-y-5">
              <div className="label-eyebrow">How to Install</div>

              <div>
                <div className="text-sm text-white mb-2 font-medium">Gmail</div>
                <ol className="text-xs text-white/60 space-y-1 list-decimal list-inside leading-relaxed">
                  <li>Click <b>Copy HTML</b> above</li>
                  <li>Gmail → <b>Settings</b> → <b>See all settings</b></li>
                  <li>Scroll to <b>Signature</b> → <b>Create new</b></li>
                  <li>Paste (⌘V / Ctrl+V) into the signature box</li>
                  <li>Set as default → <b>Save Changes</b></li>
                </ol>
              </div>

              <div>
                <div className="text-sm text-white mb-2 font-medium">Outlook (Web)</div>
                <ol className="text-xs text-white/60 space-y-1 list-decimal list-inside leading-relaxed">
                  <li>Click <b>Copy HTML</b> above</li>
                  <li>Settings ⚙️ → <b>Mail</b> → <b>Compose and reply</b></li>
                  <li>Paste into the signature editor</li>
                  <li>Tick "Auto-include on new messages"</li>
                  <li>Click <b>Save</b></li>
                </ol>
              </div>

              <div>
                <div className="text-sm text-white mb-2 font-medium">Apple Mail</div>
                <ol className="text-xs text-white/60 space-y-1 list-decimal list-inside leading-relaxed">
                  <li>Mail → <b>Settings</b> → <b>Signatures</b></li>
                  <li>Pick your account → <b>+</b> to add a new signature</li>
                  <li>Paste in — tick "Always match my default font" OFF</li>
                </ol>
              </div>
            </div>
          </aside>

          {/* Preview + Raw HTML */}
          <section className="lg:col-span-8 space-y-6">
            <div>
              <div className="label-eyebrow mb-3">Live Preview</div>
              <div
                data-testid="sig-preview"
                className="bg-white p-8 border border-white/10 rounded overflow-x-auto"
                dangerouslySetInnerHTML={{ __html: html }}
              />
              <p className="text-xs text-white/40 mt-3">
                This is exactly how your signature will look in emails you send from Keys To Own.
              </p>
            </div>

            <div>
              <div className="label-eyebrow mb-3">Raw HTML</div>
              <pre
                data-testid="sig-raw-html"
                className="bg-[#0a0a0a] border border-white/10 p-5 text-[11px] text-white/60 overflow-x-auto font-mono leading-relaxed max-h-96"
              >
                <code>{html}</code>
              </pre>
              <p className="text-xs text-white/40 mt-3 flex items-center gap-2">
                <Mail size={12} className="text-[#D4AF37]" />
                You can also select the raw HTML above and copy it manually if the button doesn't work.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
